import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const variants = {
  primary: 'bg-blue-600 hover:bg-blue-500 text-white shadow-xs',
  dark: 'bg-slate-900 hover:bg-slate-800 text-white shadow-xs',
  secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200',
  danger: 'bg-rose-600 hover:bg-rose-500 text-white shadow-md',
  'danger-soft': 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200',
  success: 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-600/20',
  ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
} as const;

const sizes = {
  sm: 'px-3 py-1.5 text-xs rounded-lg gap-1.5',
  md: 'px-3.5 py-2 text-xs rounded-xl gap-1.5',
  icon: 'p-2 rounded-xl',
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', type = 'button', className, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-bold transition-all whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  );
});
